import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import theme from "../../../../assets/theme";
import ActionBar from "../../../common/ActionBar";
import { navigate } from "@reach/router";
import _ from "lodash";
import SystemService from "../../../../services/SystemService";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import CustomTable from "../../../CustomTable/MaterialCustomerCodeTable";
import { TextField, makeStyles } from "@material-ui/core";

const useStyles = makeStyles({
  customField: {
    width: "60%",
    fontSize: "16px",
    "& .MuiOutlinedInput-root": {
      borderRadius: 6,
      height: 38,
      backgroundColor: "#fff",
      "& fieldset": {
        borderColor: "#8294a5",
        borderWidth: 1,
      },
      "&:hover fieldset": {
        borderColor: "#8294a5",
        borderWidth: 1,
      },
      "&.Mui-focused fieldset": {
        borderColor: "#8294a5",
        borderWidth: 1,
      },
    },
    "& input": {
      letterSpacing: 0.1,
      fontSize: "16px",
      color: `#293461 !important`,
      fontFamily: `${theme.fonts.primaryFontSemiBold}`,
    },
    "& .MuiInputLabel-outlined": {
      color: "#8294a5",
    },
    "& .Mui-focused .MuiInputLabel-outlined": {
      color: "#8294a5",
    },
  },
});

const PartyCodes = () => {
  const [partyCode, setPartyCode] = useState([]);
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [trigger, setTrigger] = useState(true);
  const [page, setPage] = useState(1);
  const MTRef = useRef();
  const classes = useStyles();

  useEffect(() => {
    async function getPartyCodes() {
      try {
        setLoader({ state: true, message: "Fetching details..." });
        const response = await SystemService.getPartyCodes();
        if (response) {
          setPartyCode(response);
        }
      } catch (err) {
        throw err;
      } finally {
        setTrigger(false);
        setLoader({ state: false });
        window.scrollTo(0, 0);
      }
    }
    if (trigger) {
      getPartyCodes();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  const onBreadcrumbClick = (selected) => {
    switch (_.snakeCase(selected)) {
      case "configurations":
        navigate("/dashboard/configurations");
        break;
      default:
        break;
    }
  };

  /**
   * @description delete ip whitelist
   */

  const deleteCustomerCodes = async (id) => {
    try {
      setLoader({ state: true, message: "delete item..." });
      if (id) {
        const response = await SystemService.deletePartyCodes(id);
        if (response) {
          enqueueSnackbar(response?.message, {
            variant: "success",
          });
        }
      }
    } catch (err) {
      enqueueSnackbar(err?.message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  };

  async function addCustomerCode(values) {
    try {
      setLoader({ state: true, message: "create party code..." });
      const payload = {
        customerCodeParams: [
          {
            customerName: values?.customerName,
            customerCode: values?.customerCode,
          },
        ],
      };
      const response = await SystemService.createPartyCode(payload);
      if (response) {
        enqueueSnackbar(response?.message, {
          variant: "success",
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      setTrigger(true);
    }
  }

  /**
   * @description update customer code
   */

  async function updatePartyCode(id, customerName, customerCode) {
    try {
      setLoader({ state: true, message: "update party code..." });
      const payload = {
        customerName: customerName,
        customerCode: customerCode,
      };
      const response = await SystemService.updatePartyCode(id, payload);
      if (response) {
        enqueueSnackbar(response?.message, {
          variant: "success",
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  }

  const columns = [
    {
      title: "Customer Name",
      field: "customerName",
      editComponent: (props) => (
        <TextField
          className={classes.customField}
          value={props.value || ""}
          onChange={(e) => props.onChange(e.target.value)}
          variant="outlined"
          size="small"
          error={false}
        />
      ),
    },
    {
      title: "Customer Code",
      field: "customerCode",
      editComponent: (props) => {
        const isNewRow = props.rowData?.tableData?.id === undefined;
        return isNewRow ? (
          <TextField
            className={classes.customField}
            value={props.value || ""}
            onChange={(e) => props.onChange(e.target.value)}
            variant="outlined"
            size="small"
            error={false}
          />
        ) : (
          <Bold>{props.value}</Bold>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <ActionBar breadcrumbs={["Customer codes"]} {...{ onBreadcrumbClick }} />
      <Padding>
        <CustomTable
          hidePagination={partyCode?.lastPage === 1}
          pageSize={10}
          pluralTitle={"Customer code"}
          singularTitle={"Customer code"}
          placeholderText={"Search"}
          noToolbar={true}
          addItem={true}
          hideFilterBar={true}
          {...{
            columns,
            page,
            setPage,
            MTRef,
          }}
          handleEdit={{
            onRowAdd: (newData) =>
              new Promise((resolve, reject) => {
                setTimeout(() => {
                  setPartyCode([...partyCode, newData]);
                  addCustomerCode(newData);
                  resolve();
                }, 100);
              }),
            onRowUpdate: (newData, oldData) =>
              new Promise((resolve, reject) => {
                setTimeout(() => {
                  const dataUpdate = [...partyCode];
                  const index = oldData.tableData.id;
                  dataUpdate[index] = newData;
                  setPartyCode([...dataUpdate]);
                  updatePartyCode(
                    dataUpdate[index]?.id,
                    dataUpdate[index]?.customerName,
                    dataUpdate[index]?.customerCode,
                  );
                  resolve();
                }, 100);
              }),
            onRowDelete: (oldData) =>
              new Promise((resolve, reject) => {
                setTimeout(() => {
                  const dataDelete = [...partyCode];
                  const index = oldData.tableData.id;
                  deleteCustomerCodes(dataDelete[index]?.id);
                  dataDelete.splice(index, 1);
                  setPartyCode([...dataDelete]);
                  resolve();
                }, 100);
              }),
          }}
          data={partyCode}
          state={partyCode}
        />
      </Padding>
    </React.Fragment>
  );
};

export default PartyCodes;

const Padding = styled.div`
  padding-top: 30px;
  padding-bottom: 50px;
  padding-left: 35px;
  padding-right: 43px;
`;

export const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 25px;
`;

export const ButtonWrapper = styled.div`
  width: fit-content;
  @media ${theme?.breakpoints?.md_up} {
    justify-content: flex-start;
    display: flex;
    width: 200px;
    padding-bottom: 20px;
    padding-top: 7px;
  }
`;

export const DeleteIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-top: 15px;
  object-fit: contain;
  cursor: pointer;
  margin-left: 10px;
`;

const Bold = styled.span`
  cursor: pointer;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;
